import { ImmutableMap } from '@mode/shared/contract-common';
import { MetricDomain } from '@mode/shared/contract-common';
import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import { Map, OrderedSet } from 'immutable';
import { isPresent } from '@mode/shared/util-js';
import * as MetricActions from './metric.actions';

export const metricFeatureKey = 'metric';

export interface State {
  // state for listing and selecting metrics for a given datasource
  datasourceMetrics: ReadonlyArray<MetricDomain.Metric>;
  isDatasourceMetricsLoading: boolean;
  isDatasourceMetricsComplete: boolean;
  isMetricsRunComplete: boolean;
  datasourceMetricsError: Error | null;

  // state for getting selected metric information for a given metric query
  selectedMetric: string | null;
  selectedVizType: MetricDomain.VizTypeSelection | null;
  selectedQueryRawSql: string | null;
  tokens: OrderedSet<string>;
  dataSourceIds: ImmutableMap<number, string>;
  names: ImmutableMap<string>;
  sqls: ImmutableMap<string>;
  types: ImmutableMap<string>;

  chartStates: ImmutableMap<MetricDomain.ChartState, string>;
}

export const initialState: State = {
  datasourceMetrics: [],
  isDatasourceMetricsLoading: false,
  isDatasourceMetricsComplete: false,
  isMetricsRunComplete: false,
  datasourceMetricsError: null,

  selectedMetric: null,
  selectedVizType: null,
  selectedQueryRawSql: null,
  dataSourceIds: Map(),
  tokens: OrderedSet(),
  names: Map(),
  sqls: Map(),
  types: Map(),

  chartStates: Map(),
};

export const datasourceMetricsReducer = createReducer(
  initialState.datasourceMetrics,
  on(MetricActions.metricDialogLoadMetrics, (state) => state),
  on(MetricActions.metricDialogLoadMetricsSuccess, (state, action) => action.metrics),
  on(MetricActions.metricDialogLoadMetricsFailure, (state, action) => []),
  on(MetricActions.metricQueryRouteLoadMetricSuccess, (state, action) =>
    state.filter((metric) => metric.id !== action.metric.id).concat(action.metric)
  )
);

export const isDatasourceMetricsLoadingReducer = createReducer(
  initialState.isDatasourceMetricsLoading,
  on(MetricActions.metricDialogLoadMetrics, (state) => true),
  on(MetricActions.metricDialogLoadMetricsSuccess, (state, action) => false),
  on(MetricActions.metricDialogLoadMetricsFailure, (state, action) => false)
);

export const isDatasourceMetricsCompleteReducer = createReducer(
  initialState.isDatasourceMetricsComplete,
  on(MetricActions.metricDialogLoadMetrics, (state) => false),
  on(MetricActions.metricDialogLoadMetricsSuccess, (state, action) => true),
  on(MetricActions.metricDialogLoadMetricsFailure, (state, action) => true),
  on(MetricActions.metricQueryRouteLoadMetricSuccess, (state, action) => true)
);

export const isMetricsRunCompleteReducer = createReducer(
  initialState.isMetricsRunComplete,
  on(MetricActions.metricDialogLoadMetrics, (state) => false),
  on(MetricActions.metricsRunComplete, (state, action) => true)
);

export const datasourceMetricsErrorReducer = createReducer(
  initialState.datasourceMetricsError,
  on(MetricActions.metricDialogLoadMetrics, (state) => null),
  on(MetricActions.metricDialogLoadMetricsSuccess, (state) => null),
  on(MetricActions.metricDialogLoadMetricsFailure, (state, action) => action.error)
);

export const selectedMetricReducer = createReducer(
  initialState.selectedMetric,
  on(MetricActions.metricQueryRouteSelectMetric, (state, action) => action.metricToken)
);

export const selectedVizTypeReducer = createReducer(
  initialState.selectedVizType,
  on(MetricActions.metricQueryRouteSelectVizType, (state, action) => action.vizType)
);

export const selectedQueryRawSqlReducer = createReducer(
  initialState.selectedQueryRawSql,
  on(MetricActions.setSelectedQueryRawSql, (_, action) => {
    return action.rawSql;
  })
);

export const dataSourceIdsReducer = createReducer(
  initialState.dataSourceIds,
  on(MetricActions.metricDialogLoadMetricsSuccess, (state, action) =>
    state.merge(action.metrics.map<[string, number]>((m) => [m.token, m.dataSourceId]))
  ),
  on(MetricActions.metricQueryRouteLoadMetricSuccess, (state, action) =>
    state.set(action.metric.token, action.metric.dataSourceId)
  )
);

export const tokensReducer = createReducer(
  initialState.tokens,
  on(MetricActions.metricDialogLoadMetricsSuccess, (state, action) =>
    state.merge(action.metrics.map((m) => m.token).filter(isPresent))
  ),
  on(MetricActions.metricQueryRouteLoadMetricSuccess, (state, action) => state.add(action.metric.token))
);

export const namesReducer = createReducer(
  initialState.names,
  on(MetricActions.metricDialogLoadMetricsSuccess, (state, action) =>
    state.merge(action.metrics.map<[string, string]>((m) => [m.token, m.name]))
  ),
  on(MetricActions.metricQueryRouteLoadMetricSuccess, (state, action) =>
    state.set(action.metric.token, action.metric.name)
  )
);

export const sqlsReducer = createReducer(
  initialState.sqls,
  on(MetricActions.metricDialogLoadMetricsSuccess, (state, action) =>
    state.merge(action.metrics.map<[string, string]>((m) => [m.token, m.sql]))
  ),
  on(MetricActions.metricQueryRouteLoadMetricSuccess, (state, action) =>
    state.set(action.metric.token, action.metric.sql)
  )
);

export const typesReducer = createReducer(
  initialState.types,
  on(MetricActions.metricDialogLoadMetricsSuccess, (state, action) =>
    state.merge(action.metrics.map<[string, string]>((m) => [m.token, m.type]))
  ),
  on(MetricActions.metricQueryRouteLoadMetricSuccess, (state, action) =>
    state.set(action.metric.token, action.metric.type)
  )
);

export const chartStatesReducer = createReducer(
  initialState.chartStates,
  on(MetricActions.setChartState, (state, action) => {
    return state.set(action.token, action.state);
  }),
  on(MetricActions.initChartStateIfUnset, (state, action) => {
    return state.set(action.token, state.get(action.token) ?? action.state);
  })
);

export const reducer: ActionReducerMap<State> = {
  datasourceMetrics: datasourceMetricsReducer,
  isDatasourceMetricsLoading: isDatasourceMetricsLoadingReducer,
  isDatasourceMetricsComplete: isDatasourceMetricsCompleteReducer,
  datasourceMetricsError: datasourceMetricsErrorReducer,
  isMetricsRunComplete: isMetricsRunCompleteReducer,

  selectedMetric: selectedMetricReducer,
  selectedVizType: selectedVizTypeReducer,
  selectedQueryRawSql: selectedQueryRawSqlReducer,
  dataSourceIds: dataSourceIdsReducer,
  tokens: tokensReducer,
  names: namesReducer,
  sqls: sqlsReducer,
  types: typesReducer,

  chartStates: chartStatesReducer,
};
